// src/App.js
import React, { useEffect, useState } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Outlet,
  useLocation,
} from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { StatusBar, Style } from "@capacitor/status-bar";

import { SettingsProvider } from "./settings";
import { AuthProvider, useAuth } from "./auth/AuthProvider";
import AppBootstrap from "./AppBootstrap";

import { apiFetch } from "./api";

import BottomTabsLayout from "./screens/BottomTabsLayout";
import AssetDashboard from "./screens/AssetDashboard";
import ConfigScreen from "./screens/ConfigScreen";
import Today from "./screens/Today";
import Monitoring from "./screens/MonitoringDashboard";
import PrivacyPolicy from "./screens/PrivacyPolicy";
import Support from "./screens/Support";

import OnboardingTerms from "./screens/OnboardingTerms";
import Home from "./screens/Home";
import BuyAndSell from "./screens/BuyAndSell";

import LoginEmail from "./screens/LoginEmail";
import LoginOtp from "./screens/LoginOtp";

import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "./queryClient";

/* ============================================================
   Auth guard
============================================================ */
function RequireAuth() {
  const { isAuthed } = useAuth();
  const loc = useLocation();

  if (!isAuthed) {
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: loc.pathname + loc.search }}
      />
    );
  }
  return <Outlet />;
}

/* ============================================================
   Terms guard (onboarding)
   - checa no backend 1x por usuário (customerId)
   - cache em sessionStorage/localStorage para evitar refetch e evitar loop em falha de rede
   - ✅ destrava imediatamente quando a tela de termos dispara evento
   - ✅ redirecionamento usa accepted EFFECTIVE (status OU cache)
============================================================ */
function RequireTermsAccepted() {
  const { isAuthed, auth } = useAuth();
  const loc = useLocation();

  const customerId = auth?.customerId ?? null;

  const [status, setStatus] = useState({
    loading: true,
    accepted: false,
    checkedForCustomerId: null,
  });

  useEffect(() => {
    let alive = true;

    function getCachedAccepted(cid) {
      if (!cid) return false;

      // ✅ legado (sempre gravar isso no accept)
      const legacyKey = `termsAccepted:${cid}`;
      const legacy =
        sessionStorage.getItem(legacyKey) || localStorage.getItem(legacyKey);
      if (legacy === "1") return true;

      // versionado pode existir, mas sem requiredVersion não dá para checar aqui
      return false;
    }

    function onTermsAccepted(e) {
      const cid = e?.detail?.customerId;
      if (!cid || cid !== customerId) return;

      if (!alive) return;
      setStatus({
        loading: false,
        accepted: true,
        checkedForCustomerId: cid,
      });
    }

    window.addEventListener("finbot_terms_accepted", onTermsAccepted);

    (async () => {
      if (!isAuthed || !customerId) {
        if (alive) {
          setStatus({
            loading: false,
            accepted: false,
            checkedForCustomerId: null,
          });
        }
        return;
      }

      // ✅ 1) libera imediatamente se já tem cache legado
      const cached = getCachedAccepted(customerId);
      if (cached && alive) {
        setStatus({
          loading: false,
          accepted: true,
          checkedForCustomerId: customerId,
        });
        // continua e revalida no backend (best-effort)
      } else if (alive) {
        setStatus({
          loading: true,
          accepted: false,
          checkedForCustomerId: customerId,
        });
      }

      // ✅ 2) fonte da verdade (backend)
      try {
        const s = await apiFetch("/api/onboarding/status");
        if (!alive) return;

        const requiredVersion = String(s?.requiredVersion || "").trim();
        const accepted = !!s?.accepted;

        const versionedKey = requiredVersion
          ? `termsAccepted:${customerId}:${requiredVersion}`
          : null;

        if (accepted) {
          // grava versionado (se houver)
          if (versionedKey) {
            sessionStorage.setItem(versionedKey, "1");
            localStorage.setItem(versionedKey, "1");
          }
          // ✅ grava legado sempre (compat / fallback)
          sessionStorage.setItem(`termsAccepted:${customerId}`, "1");
          localStorage.setItem(`termsAccepted:${customerId}`, "1");
        }

        setStatus({
          loading: false,
          accepted,
          checkedForCustomerId: customerId,
        });
      } catch {
        if (!alive) return;

        // falha rede: respeita cache legado
        const acceptedFromCache = getCachedAccepted(customerId);

        setStatus({
          loading: false,
          accepted: acceptedFromCache,
          checkedForCustomerId: customerId,
        });
      }
    })();

    return () => {
      alive = false;
      window.removeEventListener("finbot_terms_accepted", onTermsAccepted);
    };
  }, [isAuthed, customerId]);

  if (!isAuthed) return <Outlet />;
  if (!customerId) return null;

  // ✅ accepted efetivo = state OU cache (evita loop entre aceitar e re-render)
  const acceptedEffective =
    status.accepted ||
    (() => {
      try {
        const legacyKey = `termsAccepted:${customerId}`;
        const legacy =
          sessionStorage.getItem(legacyKey) || localStorage.getItem(legacyKey);
        return legacy === "1";
      } catch {
        return false;
      }
    })();

  if (status.loading && !acceptedEffective) return null;

  if (!acceptedEffective && !loc.pathname.startsWith("/onboarding/terms")) {
    return <Navigate to="/onboarding/terms" replace />;
  }

  return <Outlet />;
}

/* ============================================================
   App root
============================================================ */
export default function App() {
  useEffect(() => {
    if (Capacitor.getPlatform() === "ios") {
      (async () => {
        try {
          await StatusBar.setOverlaysWebView({ overlay: false });
          await StatusBar.setStyle({ style: Style.Light });
          await StatusBar.setBackgroundColor({ color: "#0a0a0c" });
        } catch (e) {
          console.log("StatusBar error", e);
        }
      })();
    }
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <SettingsProvider>
        <AuthProvider>
          <AppBootstrap />

          <BrowserRouter>
            <Routes>
              {/* públicas */}
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/support" element={<Support />} /> 
              <Route path="/login" element={<LoginEmail />} />
              <Route path="/login/otp" element={<LoginOtp />} />

              {/* protegidas */}
              <Route element={<RequireAuth />}>
                <Route element={<RequireTermsAccepted />}>
                  {/* 🔑 onboarding (sem tab) */}
                  <Route path="/onboarding/terms" element={<OnboardingTerms />} />

                  {/* Layout com tabs */}
                  <Route element={<BottomTabsLayout />}>
                    <Route index element={<Navigate to="/today" replace />} />
                    <Route path="/home" element={<Home />} />
                    <Route path="/today" element={<Today />} />
                    <Route path="/buyandsell" element={<BuyAndSell />} />
                    <Route path="/monitoring" element={<Monitoring />} />
                    <Route path="/config" element={<ConfigScreen />} />
                  </Route>

                  {/* 🔑 rota oculta (sem tab) */}
                  <Route path="/assets" element={<AssetDashboard />} />
                </Route>
              </Route>

              {/* fallback */}
              <Route path="*" element={<Navigate to="/today" replace />} />
            </Routes>
          </BrowserRouter>
        </AuthProvider>
      </SettingsProvider>
    </QueryClientProvider>
  );
}
